import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, CheckCircle, Clock, AlertCircle, ArrowUpDown, X, Upload, FileText, Calendar, AlertTriangle, Package } from 'lucide-react';

const EOLAssets = ({ user }) => {
  const [assets, setAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [sortField, setSortField] = useState('created_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // Asset lookup states
  const [assetSearch, setAssetSearch] = useState('');
  const [assetOptions, setAssetOptions] = useState([]);
  const [showAssetDropdown, setShowAssetDropdown] = useState(false);
  const [assetSearchLoading, setAssetSearchLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    letter_type: 'End of Life',
    name: '',
    asset: '',
    location: '',
    manufacture: '',
    model: '',
    date_of_purchase: '',
    serialnum: '',
    service_provider: 'In-House',
    notes: ''
  });

  // Files state
  const [files, setFiles] = useState([]);
  const [fileUpload, setFileUpload] = useState({
    file: null,
    title: '',
    expiration_date: ''
  });

  // Counts state
  const [counts, setCounts] = useState({
    draft: 0,
    pending_files: 0,
    files_uploaded: 0,
    complete: 0,
    total: 0,
    expired_files: 0,
    expiring_files: 0,
    total_alerts: 0
  });

  // Asset search function
  const searchInventoryAssets = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setAssetOptions([]);
      setShowAssetDropdown(false);
      return;
    }

    setAssetSearchLoading(true);
    try {
      const response = await fetch(`/api/inventory-assets/search?search=${encodeURIComponent(searchTerm)}`);
      const data = await response.json();
      
      if (data.success) {
        setAssetOptions(data.assets || []);
        setShowAssetDropdown(true);
      }
    } catch (error) {
      console.error('Asset search error:', error);
      setAssetOptions([]);
    }
    setAssetSearchLoading(false);
  };

  // Handle asset selection from dropdown
  const selectInventoryAsset = (asset) => {
    setFormData(prev => ({
      ...prev,
      asset: asset.name || '',
      name: asset.type || '',
      location: asset.work_location || '',
      manufacture: asset.manufacturer || '',
      model: asset.model || '',
      serialnum: asset.serial || ''
    }));
    
    setAssetSearch(asset.name || '');
    setShowAssetDropdown(false);
    setAssetOptions([]);
  };

  // Clear asset selection
  const clearAssetSelection = () => {
    setAssetSearch('');
    setAssetOptions([]);
    setShowAssetDropdown(false);
    setFormData(prev => ({
      ...prev,
      asset: '',
      name: '',
      location: '',
      manufacture: '',
      model: '',
      serialnum: ''
    }));
  };

  // Fetch assets
  const fetchAssets = async () => {
    setLoading(true);
    try {
      console.log('=== FETCHING EOL ASSETS ===');
      console.log('Filter:', activeFilter);
      
      const response = await fetch(`/api/eol-assets?status=${activeFilter === 'alerts' ? 'all' : activeFilter}&filter=${activeFilter === 'alerts' ? 'alerts' : ''}`);
      const data = await response.json();
      
      setAssets(data.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching EOL assets:', error);
      setLoading(false);
      setAssets([]);
    }
  };

  // Fetch counts
  const fetchCounts = async () => {
    try {
      const response = await fetch('/api/eol-assets/counts');
      const data = await response.json();
      setCounts(data);
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  };

  // Fetch files for asset
  const fetchFiles = async (assetId) => {
    try {
      const response = await fetch(`/api/eol-assets/${assetId}/files`);
      const data = await response.json();
      if (data.success) {
        setFiles(data.files);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  // Filter and sort assets
  const filterAndSortAssets = () => {
    let filtered = [...assets];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(asset =>
        asset.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        asset.asset?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        asset.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        asset.manufacture?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        asset.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        asset.letter_type?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      
      if (sortField === 'created_date') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredAssets(filtered);
    setCurrentPage(1);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle file upload input changes
  const handleFileInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'file') {
      setFileUpload(prev => ({
        ...prev,
        file: files[0]
      }));
    } else {
      setFileUpload(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Asset search input handler
  const handleAssetSearchChange = (e) => {
    const value = e.target.value;
    setAssetSearch(value);
    searchInventoryAssets(value);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      letter_type: 'End of Life',
      name: '',
      asset: '',
      location: '',
      manufacture: '',
      model: '',
      date_of_purchase: '',
      serialnum: '',
      service_provider: 'In-House',
      notes: ''
    });
    setSelectedAsset(null);
    setAssetSearch('');
    setAssetOptions([]);
    setShowAssetDropdown(false);
  };

  // Reset file upload form
  const resetFileForm = () => {
    setFileUpload({
      file: null,
      title: '',
      expiration_date: ''
    });
  };

  // Open modal
  const openModal = (mode, asset = null) => {
    setModalMode(mode);
    if (mode === 'edit' && asset) {
      setSelectedAsset(asset);
      setFormData({
        letter_type: asset.letter_type || 'End of Life',
        name: asset.name || '',
        asset: asset.asset || '',
        location: asset.location || '',
        manufacture: asset.manufacture || '',
        model: asset.model || '',
        date_of_purchase: asset.date_of_purchase || '',
        serialnum: asset.serialnum || '',
        service_provider: asset.service_provider || 'In-House',
        notes: asset.notes || ''
      });
      setAssetSearch(asset.asset || '');
    } else if (mode === 'files' && asset) {
      setSelectedAsset(asset);
      fetchFiles(asset.id);
      setShowFilesModal(true);
      return;
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setShowFilesModal(false);
    resetForm();
    resetFileForm();
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        userId: user.id,
        userName: user.fullName
      };

      const url = modalMode === 'add' ? '/api/eol-assets' : `/api/eol-assets/${selectedAsset.id}`;
      const method = modalMode === 'add' ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.success) {
        closeModal();
        await Promise.all([fetchAssets(), fetchCounts()]);
      } else {
        alert('Error: ' + result.error);
      }
    } catch (error) {
      console.error('Submit error:', error);
      alert('Error: ' + error.message);
    }
  };

  // Upload file
  const handleFileUpload = async (e) => {
    e.preventDefault();
    
    if (!fileUpload.file || !fileUpload.title || !fileUpload.expiration_date) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', fileUpload.file);
      formData.append('title', fileUpload.title);
      formData.append('expiration_date', fileUpload.expiration_date);
      formData.append('userId', user.id);
      formData.append('userName', user.fullName);

      const response = await fetch(`/api/eol-assets/${selectedAsset.id}/files`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        resetFileForm();
        fetchFiles(selectedAsset.id);
        fetchCounts();
      } else {
        alert('Error: ' + result.error);
      }
    } catch (error) {
      console.error('File upload error:', error);
      alert('Error: ' + error.message);
    }
  };

  // Delete file
  const deleteFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      const response = await fetch(`/api/eol-assets/${selectedAsset.id}/files/${fileId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          userName: user.fullName
        })
      });

      const result = await response.json();

      if (result.success) {
        fetchFiles(selectedAsset.id);
        fetchCounts();
      } else {
        alert('Error: ' + result.error);
      }
    } catch (error) {
      console.error('Delete file error:', error);
      alert('Error: ' + error.message);
    }
  };

  // Change status
  const changeStatus = async (assetId, newStatus) => {
    if (!window.confirm(`Change status to ${newStatus.replace('_', ' ')}?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/eol-assets/${assetId}/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: newStatus,
          userId: user.id,
          userName: user.fullName
        })
      });

      const result = await response.json();

      if (result.success) {
        await Promise.all([fetchAssets(), fetchCounts()]);
      } else {
        alert('Error: ' + result.error);
      }
    } catch (error) {
      console.error('Status change error:', error);
      alert('Error: ' + error.message);
    }
  };

  // Delete asset
  const deleteAsset = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this EOL asset? This will also delete all associated files.')) {
      return;
    }

    try {
      const response = await fetch(`/api/eol-assets/${assetId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          userName: user.fullName
        })
      });

      const result = await response.json();

      if (result.success) {
        await Promise.all([fetchAssets(), fetchCounts()]);
      } else {
        alert('Error: ' + result.error);
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Error: ' + error.message);
    }
  };

  // useEffect hooks
  useEffect(() => {
    fetchCounts();
    fetchAssets();
  }, [activeFilter]);

  useEffect(() => {
    filterAndSortAssets();
  }, [assets, searchTerm, sortField, sortDirection]);

  // Helper functions
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft': return <AlertCircle className="w-4 h-4 text-gray-500" />;
      case 'pending_files': return <Clock className="w-4 h-4 text-blue-500" />;
      case 'files_uploaded': return <FileText className="w-4 h-4 text-orange-500" />;
      case 'complete': return <CheckCircle className="w-4 h-4 text-green-500" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'pending_files': return 'bg-blue-100 text-blue-800';
      case 'files_uploaded': return 'bg-orange-100 text-orange-800';
      case 'complete': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'draft': return 'Draft';
      case 'pending_files': return 'Pending Files';
      case 'files_uploaded': return 'Files Uploaded';
      case 'complete': return 'Complete';
      default: return 'Unknown';
    }
  };

  const getFileStatusColor = (status) => {
    switch (status) {
      case 'expired': return 'text-red-600 bg-red-50';
      case 'expiring': return 'text-yellow-600 bg-yellow-50';
      case 'current': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getFileStatusIcon = (status) => {
    switch (status) {
      case 'expired': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'expiring': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'current': return <CheckCircle className="w-4 h-4 text-green-500" />;
      default: return <FileText className="w-4 h-4 text-gray-400" />;
    }
  };

  const getAssetAlertStatus = (asset) => {
    if (asset.expired_files > 0) return 'expired';
    if (asset.expiring_files > 0) return 'expiring';
    return 'current';
  };

  // Pagination
  const totalPages = Math.ceil(filteredAssets.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAssets = filteredAssets.slice(startIndex, endIndex);

  // Status filters
  const filters = [
    { key: 'all', label: 'All Assets', count: counts.total },
    { key: 'alerts', label: 'File Alerts', count: counts.total_alerts, color: 'text-red-600' },
    { key: 'draft', label: 'Draft', count: counts.draft },
    { key: 'pending_files', label: 'Pending Files', count: counts.pending_files },
    { key: 'files_uploaded', label: 'Files Uploaded', count: counts.files_uploaded },
    { key: 'complete', label: 'Complete', count: counts.complete }
  ];

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h1 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem' }}>NRMC EOL Assets</h1>
          <p style={{ color: '#6b7280' }}>Manage end-of-life equipment and documentation</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={() => openModal('add')}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}
          >
            <Plus size={16} />
            Add EOL Asset
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {filters.map(filter => (
          <button
            key={filter.key}
            onClick={() => setActiveFilter(filter.key)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              border: '1px solid #d1d5db',
              background: activeFilter === filter.key ? '#2563eb' : 'white',
              color: activeFilter === filter.key ? 'white' : (filter.color || '#374151'),
              cursor: 'pointer',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {filter.label}
            <span style={{
              background: activeFilter === filter.key ? 'rgba(255,255,255,0.2)' : '#e5e7eb',
              padding: '0.125rem 0.375rem',
              borderRadius: '0.25rem',
              fontSize: '0.75rem',
              fontWeight: '600',
              color: activeFilter === filter.key ? 'white' : (filter.color || '#374151')
            }}>
              {filter.count}
            </span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ position: 'relative', maxWidth: '400px' }}>
          <Search size={20} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            type="text"
            placeholder="Search EOL assets, locations, manufacturers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 0.75rem 0.75rem 2.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem'
            }}
          />
        </div>
      </div>

      {/* Results count */}
      <div style={{ marginBottom: '1rem', color: '#6b7280', fontSize: '0.875rem' }}>
        Showing {currentAssets.length} of {filteredAssets.length} EOL assets
      </div>

      {/* Table */}
      <div style={{ background: 'white', borderRadius: '0.5rem', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: '0.875rem' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>
                  <button
                    onClick={() => handleSort('status')}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}
                  >
                    Status <ArrowUpDown size={14} />
                  </button>
                </th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>
                  <button
                    onClick={() => handleSort('letter_type')}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}
                  >
                    Letter Type <ArrowUpDown size={14} />
                  </button>
                </th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>
                  <button
                    onClick={() => handleSort('name')}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}
                  >
                    Equipment <ArrowUpDown size={14} />
                  </button>
                </th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Asset & Location</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Manufacturer & Model</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Service Provider</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Files & Alerts</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                    Loading EOL assets...
                  </td>
                </tr>
              ) : currentAssets.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                    No EOL assets found matching your criteria
                  </td>
                </tr>
              ) : (
                currentAssets.map(asset => (
                  <tr key={asset.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {getStatusIcon(asset.status)}
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          fontWeight: '500'
                        }} className={getStatusColor(asset.status)}>
                          {getStatusText(asset.status)}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <span style={{ fontWeight: '500', color: asset.letter_type === 'End of Life' ? '#dc2626' : asset.letter_type === 'End of Service' ? '#f59e0b' : '#2563eb' }}>
                        {asset.letter_type}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ fontWeight: '500' }}>{asset.name}</div>
                      {asset.notes && (
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                          {asset.notes.substring(0, 50)}{asset.notes.length > 50 ? '...' : ''}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {asset.asset && (
                        <div style={{ fontWeight: '500', color: '#2563eb' }}>Asset: {asset.asset}</div>
                      )}
                      <div>{asset.location}</div>
                      {asset.serialnum && (
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>S/N: {asset.serialnum}</div>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ fontWeight: '500' }}>{asset.manufacture}</div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{asset.model}</div>
                      {asset.date_of_purchase && (
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          Purchased: {new Date(asset.date_of_purchase).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        background: asset.service_provider === 'In-House' ? '#dbeafe' : asset.service_provider === 'OEM' ? '#dcfce7' : '#fef3c7',
                        color: asset.service_provider === 'In-House' ? '#1e40af' : asset.service_provider === 'OEM' ? '#166534' : '#92400e'
                      }}>
                        {asset.service_provider}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '0.875rem' }}>
                          {asset.file_count || 0} files
                        </span>
                        {(asset.expired_files > 0 || asset.expiring_files > 0) && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            {getFileStatusIcon(getAssetAlertStatus(asset))}
                            <span style={{ fontSize: '0.75rem' }} className={getFileStatusColor(getAssetAlertStatus(asset))}>
                              {asset.expired_files > 0 ? `${asset.expired_files} expired` : `${asset.expiring_files} expiring`}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => openModal('edit', asset)}
                          style={{ padding: '0.25rem 0.5rem', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '0.25rem', fontSize: '0.75rem', cursor: 'pointer' }}
                        >
                          <Edit size={12} />
                        </button>
                        
                        <button
                          onClick={() => openModal('files', asset)}
                          style={{ padding: '0.25rem 0.5rem', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: '0.25rem', fontSize: '0.75rem', cursor: 'pointer' }}
                        >
                          <FileText size={12} />
                        </button>

                        {asset.status !== 'complete' && (
                          <button
                            onClick={() => changeStatus(asset.id, 'complete')}
                            style={{ padding: '0.25rem 0.5rem', background: '#16a34a', color: 'white', border: 'none', borderRadius: '0.25rem', fontSize: '0.75rem', cursor: 'pointer' }}
                          >
                            Complete
                          </button>
                        )}
                        
                        <button
                          onClick={() => deleteAsset(asset.id)}
                          style={{ padding: '0.25rem 0.5rem', background: '#dc2626', color: 'white', border: 'none', borderRadius: '0.25rem', fontSize: '0.75rem', cursor: 'pointer' }}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ padding: '1rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              Page {currentPage} of {totalPages}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  background: 'white',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1
                }}
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  background: 'white',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages ? 0.5 : 1
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: '0.5rem', padding: '2rem', maxWidth: '700px', width: '90vw', maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '600' }}>
                {modalMode === 'add' ? 'Add New EOL Asset' : 'Edit EOL Asset'}
              </h3>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Asset Lookup Section */}
              <div style={{ background: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1.5rem' }}>
                <h4 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.75rem', color: '#0c4a6e', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Package size={16} />
                  Select from Inventory (Optional)
                </h4>
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder="Search by asset tag, manufacturer, model, or location..."
                      value={assetSearch}
                      onChange={handleAssetSearchChange}
                      style={{ 
                        flex: 1,
                        padding: '0.5rem', 
                        border: '1px solid #d1d5db', 
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem'
                      }}
                    />
                    {assetSearch && (
                      <button
                        type="button"
                        onClick={clearAssetSelection}
                        style={{ 
                          padding: '0.5rem', 
                          background: '#6b7280', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: '0.375rem',
                          cursor: 'pointer'
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  
                  {/* Asset Dropdown */}
                  {showAssetDropdown && (
                    <div style={{ 
                      position: 'absolute', 
                      top: '100%', 
                      left: 0, 
                      right: 0, 
                      background: 'white', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '0.375rem',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}>
                      {assetSearchLoading ? (
                        <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280' }}>
                          Searching...
                        </div>
                      ) : assetOptions.length === 0 ? (
                        <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280' }}>
                          No assets found
                        </div>
                      ) : (
                        assetOptions.map((asset, index) => (
                          <div
                            key={index}
                            onClick={() => selectInventoryAsset(asset)}
                            style={{ 
                              padding: '0.75rem', 
                              borderBottom: '1px solid #f3f4f6',
                              cursor: 'pointer',
                              transition: 'background-color 0.15s'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                          >
                            <div style={{ fontWeight: '500', color: '#2563eb' }}>
                              {asset.name}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                              {asset.manufacturer} {asset.model} • {asset.work_location}
                            </div>
                            {asset.serial && (
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                S/N: {asset.serial}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                  Start typing to search inventory assets. Selecting an asset will auto-fill the form below.
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Letter Type *</label>
                  <select
                    name="letter_type"
                    value={formData.letter_type}
                    onChange={handleInputChange}
                    required
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                  >
                    <option value="End of Life">End of Life</option>
                    <option value="End of Service">End of Service</option>
                    <option value="No Parts Available">No Parts Available</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Service Provider *</label>
                  <select
                    name="service_provider"
                    value={formData.service_provider}
                    onChange={handleInputChange}
                    required
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                  >
                    <option value="In-House">In-House</option>
                    <option value="3rd-Party">3rd-Party</option>
                    <option value="OEM">OEM</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Equipment Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    style={{ 
                      width: '100%', 
                      padding: '0.5rem', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '0.375rem',
                      backgroundColor: formData.name && assetSearch ? '#f0f9ff' : 'white'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Asset Tag</label>
                  <input
                    type="text"
                    name="asset"
                    value={formData.asset}
                    onChange={handleInputChange}
                    style={{ 
                      width: '100%', 
                      padding: '0.5rem', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '0.375rem',
                      backgroundColor: formData.asset && assetSearch ? '#f0f9ff' : 'white'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Location *</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    style={{ 
                      width: '100%', 
                      padding: '0.5rem', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '0.375rem',
                      backgroundColor: formData.location && assetSearch ? '#f0f9ff' : 'white'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Serial Number</label>
                  <input
                    type="text"
                    name="serialnum"
                    value={formData.serialnum}
                    onChange={handleInputChange}
                    style={{ 
                      width: '100%', 
                      padding: '0.5rem', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '0.375rem',
                      backgroundColor: formData.serialnum && assetSearch ? '#f0f9ff' : 'white'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Manufacturer *</label>
                  <input
                    type="text"
                    name="manufacture"
                    value={formData.manufacture}
                    onChange={handleInputChange}
                    required
                    style={{ 
                      width: '100%', 
                      padding: '0.5rem', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '0.375rem',
                      backgroundColor: formData.manufacture && assetSearch ? '#f0f9ff' : 'white'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Model *</label>
                  <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    required
                    style={{ 
                      width: '100%', 
                      padding: '0.5rem', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '0.375rem',
                      backgroundColor: formData.model && assetSearch ? '#f0f9ff' : 'white'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Date of Purchase</label>
                <input
                  type="date"
                  name="date_of_purchase"
                  value={formData.date_of_purchase}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="3"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <button
                  type="button"
                  onClick={closeModal}
                  style={{ padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', background: 'white', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: '0.5rem 1rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}
                >
                  {modalMode === 'add' ? 'Add EOL Asset' : 'Update EOL Asset'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Files Modal */}
      {showFilesModal && selectedAsset && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: 'white', borderRadius: '0.5rem', padding: '2rem', maxWidth: '800px', width: '90vw', maxHeight: '90vh', overflowY: 'auto', overflowX: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '600' }}>
                Files for {selectedAsset.name}
              </h3>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>

            {/* Upload Form */}
            <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1.5rem' }}>
              <h4 style={{ marginBottom: '1rem', fontWeight: '600' }}>Upload New File</h4>
              <form onSubmit={handleFileUpload}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>File Title *</label>
                    <input
                      type="text"
                      name="title"
                      value={fileUpload.title}
                      onChange={handleFileInputChange}
                      required
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Expiration Date *</label>
                    <input
                      type="date"
                      name="expiration_date"
                      value={fileUpload.expiration_date}
                      onChange={handleFileInputChange}
                      required
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>File *</label>
                  <input
                    type="file"
                    name="file"
                    onChange={handleFileInputChange}
                    required
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                  />
                </div>
                <button
                  type="submit"
                  style={{ padding: '0.5rem 1rem', background: '#16a34a', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}
                >
                  <Upload size={16} style={{ marginRight: '0.5rem' }} />
                  Upload File
                </button>
              </form>
            </div>

            {/* Files List */}
            <div>
              <h4 style={{ marginBottom: '1rem', fontWeight: '600' }}>Current Files</h4>
              {files.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>No files uploaded yet</p>
              ) : (
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', overflowX: 'auto', width: '100%' }}>
                  <table style={{ width: '100%', fontSize: '0.875rem' }}>
                    <thead style={{ background: '#f9fafb' }}>
                      <tr>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Status</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Title</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>File</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Uploaded</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Expiration</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {files.map(file => (
                        <tr key={file.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '0.75rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              {getFileStatusIcon(file.status)}
                              <span style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '0.25rem',
                                fontSize: '0.75rem',
                                fontWeight: '500'
                              }} className={getFileStatusColor(file.status)}>
                                {file.status === 'expired' ? 'Expired' : file.status === 'expiring' ? 'Expiring' : 'Current'}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '0.75rem', fontWeight: '500' }}>{file.title}</td>
                          <td style={{ padding: '0.75rem' }}>
                            <div>{file.original_name}</div>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                              {(file.file_size / 1024).toFixed(1)} KB
                            </div>
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            <div>{new Date(file.upload_date).toLocaleDateString()}</div>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>by {file.uploaded_by}</div>
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            <div style={{ 
                              color: file.status === 'expired' ? '#dc2626' : file.status === 'expiring' ? '#f59e0b' : '#16a34a',
                              fontWeight: '500'
                            }}>
                              {new Date(file.expiration_date).toLocaleDateString()}
                            </div>
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <a
                                href={`/uploads/${file.filename}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ padding: '0.25rem 0.5rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '0.25rem', fontSize: '0.75rem', textDecoration: 'none' }}
                              >
                                View
                              </a>
                              <button
                                onClick={() => deleteFile(file.id)}
                                style={{ padding: '0.25rem 0.5rem', background: '#dc2626', color: 'white', border: 'none', borderRadius: '0.25rem', fontSize: '0.75rem', cursor: 'pointer' }}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button
                onClick={closeModal}
                style={{ padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', background: 'white', cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EOLAssets;